import { useEffect } from 'react';

import { AppRoutes } from 'config';
import { useNavigate } from 'hooks';
import { ButtonColors } from 'types';
import { Icon } from 'components/icon';
import { Popup } from 'components/popup';
import { Button } from 'components/button';
import { useAppDispatch, useAppSelector } from 'store';
import {
  fetchNotifications,
  notificationsStore,
} from 'store/slices/notifications';

import * as Styles from './styles';
import { NotificationCard } from './card';
import { NotificationHeader } from './header';
import { useManagerCompletionAlertsQuery } from 'store/api/cart';
import { CompletionAlertCard } from './completion-alert-card';
import { selectAuthUserRole } from 'store/slices/auth';
import { isTimeOffApproverRole } from 'utils/time-off';
import {
  TIME_OFF_POLL_MS,
  useTimeOffSettingsQuery,
} from 'store/api/time-off';
import { TimeOffWaitingCard } from './time-off-waiting-card';

export const NotificationsPopup = ({ generalNotifications = true }: { generalNotifications?: boolean }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isLoading, unreadCount, notifications } =
    useAppSelector(notificationsStore);

  const { data: alerts = [] } = useManagerCompletionAlertsQuery(undefined, {
    pollingInterval: 30000, refetchOnFocus: true,
  });
  // TIME OFF (plan §5): approver roles read the switch once; only while it
  // is on is the waiting count polled. Every other role skips the query.
  const approverRole = isTimeOffApproverRole(useAppSelector(selectAuthUserRole));
  const { data: timeOff } = useTimeOffSettingsQuery(undefined, {
    skip: !approverRole,
  });
  useTimeOffSettingsQuery(undefined, {
    refetchOnFocus: true,
    pollingInterval: TIME_OFF_POLL_MS,
    skip: !approverRole || !timeOff?.enabled,
  });
  const timeOffWaiting =
    timeOff?.enabled && timeOff.isApprover ? timeOff.waitingCount : 0;
  useEffect(() => {
    if (generalNotifications) dispatch(fetchNotifications());
  }, [generalNotifications, dispatch]);
  const visibleNotifications = generalNotifications ? notifications : [];
  if (!generalNotifications && !alerts.length && !timeOffWaiting) return null;

  return (
    <Popup
      removeAutofocus
      position={['bottom center', 'bottom left', 'bottom right']}
      trigger={
        <Styles.StyledButton
          padding="0"
          transparent
          icon={<Icon.Bell />}
          disabled={generalNotifications && isLoading && !alerts.length && !timeOffWaiting}
          title="Notifications"
          hasUnreadCount={!!alerts.length || !!timeOffWaiting || (generalNotifications && !!unreadCount)}
          colorType={ButtonColors.BlackWhite}
        />
      }
    >
      {(close) =>
        (alerts.length || timeOffWaiting || visibleNotifications?.length) ? (
          <Styles.Container>
            {generalNotifications && <NotificationHeader isDialog unreadCount={unreadCount} />}
            <Styles.Content>
              {timeOffWaiting > 0 && <TimeOffWaitingCard count={timeOffWaiting} close={close} />}
              {alerts.map(alert => <CompletionAlertCard key={alert.id} alert={alert} close={close} />)}
              {visibleNotifications?.map((notification, i) => (
                <NotificationCard
                  isDialog
                  close={close}
                  showDivider={i > 0 || alerts.length > 0 || timeOffWaiting > 0}
                  key={notification.id}
                  notification={notification}
                />
              ))}
            </Styles.Content>

            {generalNotifications && <Button
              transparent
              padding="16px"
              text="View all"
              colorType={ButtonColors.LightBlue}
              onClick={() => {
                close();
                navigate(AppRoutes.Notifications.Home);
              }}
            />}
          </Styles.Container>
        ) : (
          <Styles.StyledPlaceholder text="No notifications" />
        )
      }
    </Popup>
  );
};

export { NotificationCard, NotificationHeader };
